<template>
  <section
    class="spa-soft component"
  >
    <BookingHeader
      :languages="languages"
      :additional-data="bookingHeaderAdditionalData"
      :skip-booking-tracking="true"
    />
    <SpaSoftProgressBar />
    <template v-if="isDataLoaded">
      <SpaSoftHeaderDetail />
      <SpaSoftMasthead
        v-if="shouldShowMastheadAndTitle"
      />
      <transition name="fade">
        <router-view
          v-bind="bindings"
          class="spa-soft-page"
        />
      </transition>
      <SpaSoftFooter />
    </template>
    <template v-if="isLocalhostWithoutHotelCode">
      <h4 class="container">
        localhost: Hotel code is missing in the URL query, example: ?hotelCode=510
      </h4>
    </template>
    <overlay
      overlay-id="spa-soft"
      overlay-background-color="black"
      overlay-background-opacity="0.72"
      :content-width="800"
      :content-height="isMobile ? 420 : 800"
    >
      <div
        role="status"
        class="overlay-wrapper"
      >
        <div class="overlay-image icon-fan-logo" />
        <div
          id="check-availability-title"
          class="sr-only overlay-title"
        >
          {{ tc('spaSoft.step1.overlayLoading') }}
        </div>
      </div>
    </overlay>
  </section>
</template>

<script>
  import { mapState, mapActions, mapGetters } from 'vuex'
  import BookingHeader from '../../04.booking/booking-wizard/components/booking-header/booking-header.vue'
  import SpaSoftProgressBar from './components/spa-soft-progress-bar/spa-soft-progress-bar.vue'
  import SpaSoftHeaderDetail from './components/spa-soft-header-detail/spa-soft-header-detail.vue'
  import SpaSoftMasthead from './components/spa-soft-masthead/spa-soft-masthead.vue'
  import SpaSoftFooter from './components/spa-soft-footer/spa-soft-footer.vue'

  /**
   * Handles SpaSoft.
   * - **:b**-property is bind to the router view page
   */
  export default {
    name: 'SpaSoft',
    components: {
      BookingHeader,
      SpaSoftProgressBar,
      SpaSoftHeaderDetail,
      SpaSoftMasthead,
      SpaSoftFooter,
    },
    provide() {
      return {
        daysAndMonths: this.daysAndMonths,
        phoneTypes: this.phoneTypes,
        genders: this.genders,
        guestTypes: this.guestTypes,
        disablePaymentValidation: this.disablePaymentValidation,
        spaReservationDetailsPage: this.spaReservationDetailsPage,
        userTitles: this.userTitles,
      }
    },
    props: {
      /**
       * Set of translated labels to be registered in the [i18n store module](/docs/api/store/i18n)
       */
      lang: {
        type: Object,
        default: () => ({})
      },
      /**
       * Labels used by the calendar to be registered in the [i18n store module](/docs/api/store/i18n)
       */
      daysAndMonths: {
        type: Object,
        default: () => ({})
      },
      /**
       * Array of currencies available on site to be registered in the [currency store module](/docs/api/store/global)
       */
      currencies: {
        type: Array,
        default: () => []
      },
      /**
       * Array of countries available on site to be registered in the [global store module](/docs/api/store/global)
       */
      countries: {
        type: Array,
        default: () => []
      },
      /**
       * Array of languages provided for the language switcher
       */
      languages: {
        type: Array,
        default: () => []
      },
      /**
       * Content for the login components (Registration, Login, and JoinFans)
       */
      loginRegistration: {
        type: Object,
        default: () => ({})
      },
      /**
       * Adds enhancements to booking header, like logout
       */
      bookingHeaderAdditionalData: {
        type: Object,
        required: true
      },
      /**
       * Array of phone types to be used in the checkout form
       */
      phoneTypes: {
        type: Array,
        default: () => []
      },
      /**
       * Array of genders to be used in the checkout form
       */
      genders: {
        type: Array,
        default: () => []
      },
      /**
       * Array of guest types to be used in the checkout form
       */
      guestTypes: {
        type: Array,
        default: () => []
      },
      /**
       * User titles
       */
      userTitles: {
        type: Array,
        default: () => []
      },
      /**
       * Flag to know if user is accessing from California (US)
       */
      isCalifornia: {
        type: Boolean,
        default: false
      },
      /**
       * Temporary Flag to disable payment validation, TODO: Remove this flag after testing
       */
      disablePaymentValidation: {
        type: Boolean,
        default: false
      },
      /**
       * The page to redirect to after the checkout is complete
       */
      spaReservationDetailsPage: {
        type: String,
        default: ''
      },

    },
    computed: {
      ...mapState('responsive', ['width']),
      ...mapGetters('i18n', ['tc']),
      ...mapGetters('currency', ['orSelected']),
      ...mapState('spaSoft', ['step', 'propertyData']),
      ...mapGetters('spaSoft', ['getStepIndexByValue', 'shouldShowMastheadAndTitle', 'numberOfGuests', 'guestsSelectedData']),
      hasHotelCode() {
        return !!this.$route.query.hotelCode
      },
      bindings() {
        return {}
      },

      isMobile() {
        return this.width <= 767
      },

      availableGenderOptions() {
        return [
          { label: this.tc('spaSoft.step1.selectedTreatments.noGenderPreferenceLabel'), value: '' },
          { label: this.tc('spaSoft.step1.selectedTreatments.preferedTherapistGenderMale'), value: 'MALE' },
          { label: this.tc('spaSoft.step1.selectedTreatments.preferedTherapistGenderFemale'), value: 'FEMALE' }]
      },
      isLocalhostWithoutHotelCode() {
        return window.location.host.includes('localhost') && !this.hasHotelCode
      },
      isDataLoaded() {
        return this.hasHotelCode && Object.keys(this.propertyData).length > 0
      }

    },
    watch: {
      /**
       * @vuese
       * We watch the route so we can keep the route and step on the store updated all the time
       */
      $route: {
        handler(to) {
          const toStep = this.getStepIndexByValue(to.name)
          if (toStep !== this.step) {
            this.$store.commit('spaSoft/UPDATE_STEP', toStep)
          }
          this.updateRoute(to)
        },
        deep: true
      },
    },
    async created() {
      await this.registerRouter({ vueRouter: this.$router, vueRoute: this.$route })
      await this.registerLang({ scope: 'spaSoft', lang: this.lang })
      await this.registerCurrencies(this.currencies)
      await this.updateAuthComponents(this.loginRegistration)
      await this.registerGenderOptions(this.availableGenderOptions)
      await this.registerCountriesData(this.countries.map((c) => ({
        Name: c.label, IsoCode: c.value, CallingCode: c.callingCode, States: c.states
      })).filter((c) => !!c.Name))
      await this.updateIsCalifornia(this.isCalifornia)

      this.$router.onReady(async () => {
        const { hotelCode } = this.$route.query
        await this.registerHotelCode(hotelCode)
        await this.getSpaSoftData()
        this.handleSessionExpiration()
        await this.validateSpaSoftStep()
        await this.validateSpaSoftRoute()

        if (this.propertyData?.currency?.isoCode) {
          await this.changeBaseCurrency({
            fromCurrency: this.propertyData.currency.isoCode,
            toCurrency: this.orSelected(this.propertyData.currency.isoCode)
          })
        }
      })
      this.hideSitecoreLayoutHeader()
    },
    methods: {
      ...mapActions('i18n', ['registerScopedLang', 'registerLang']),
      ...mapActions('currency', ['registerCurrencies', 'changeBaseCurrency', 'getRate']),
      ...mapActions('auth', ['updateAuthComponents']),
      ...mapActions('overlay', ['activateOverlay', 'deactivateOverlay']),
      ...mapActions('spaSoft', [
        'handleSessionExpiration',
        'registerHotelCode',
        'registerRouter',
        'updateRoute',
        'validateSpaSoftStep',
        'validateSpaSoftRoute',
        'registerGenderOptions',
        'getSpaSoftData',
      ]),
      ...mapActions('global', ['registerCountriesData', 'updateIsCalifornia']),
      /**
       * @vuese
       * Hide the footer from Sitecore page layout
       */
      hideSitecoreLayoutHeader() {
        const header = document.getElementById('header')
        const skipLinks = document.getElementById('skip-links')
        const main = document.querySelector('main')
        if (header) header.style.display = 'none'
        if (skipLinks) skipLinks.style.display = 'none'
        if (main) main.style.paddingTop = 0
      },
    }
  }
</script>
